import React from "react";
import classnames from "classnames";

import { RateAdjusters, rateAdjusterToLabel } from "@eave/shared/types/api";
import { formatPercent } from "@eave/shared/utils";

const styles = require("./styles.module.scss");

export type LabelWithTooltip = (string | { label: string; tooltip: string })[];

export const validateMatrix = (matrix, xLabels, yLabels): string[] => {
  const errMsgs = [];
  if (matrix.length !== yLabels.length) {
    errMsgs.push("Mismatched yLabels and matrix");
  }

  const xLabelMismatch = matrix.some(row => row.length !== xLabels.length);
  if (xLabelMismatch) {
    errMsgs.push("Mismatched xLabels and matrix");
  }

  return errMsgs;
};

/**
 * Helper UI Components
 * - EditableCell
 * - Row
 */
interface EditableCellProps {
  value: string;
  prevValue: string;
  editing: boolean;
  onChange: (e: React.ChangeEvent) => void;
}
const EditableCell: React.FC<EditableCellProps> = ({ value, prevValue, editing, onChange }) => {
  // not found rate is a placeholder the API sends when a rate is not supposed
  // to be used but needs to be there to fill in the box.
  const notFoundRate = "9999";
  return (
    <td className={classnames({ [styles.edited]: parseFloat(prevValue) !== parseFloat(value) })}>
      <div className={styles.editableCell}>
        <input
          className={classnames({ [styles.hide]: !editing }, styles.cellInput)}
          type="text"
          value={value}
          onChange={onChange}
        />
        <span
          className={classnames(styles.cellReadOnly, {
            [styles.hide]: editing,
          })}
        >
          {value === notFoundRate ? "-" : <span>{Number(value).toFixed(3)}%</span>}
        </span>
      </div>
    </td>
  );
};

interface RowProps {
  yLabel: RateAdjusters;
  rowData: string[];
  prevRowData: string[];
  editMatrix: (key: RateAdjusters, i: number, value: string) => void;
  editing;
}
const Row: React.FC<RowProps> = ({ yLabel, rowData, prevRowData, editMatrix, editing }) => {
  return (
    <tr key={yLabel}>
      <th>{rateAdjusterToLabel(yLabel)}</th>
      {rowData.map((val, i) => {
        const id = `${yLabel}-${i}`;
        const onChange = (e: React.SyntheticEvent) => {
          const target = e.target as HTMLInputElement;
          editMatrix(yLabel, i, target.value);
        };

        return (
          <EditableCell
            key={id}
            value={val}
            prevValue={prevRowData[i]}
            editing={editing}
            onChange={onChange}
          />
        );
      })}
    </tr>
  );
};

type DataMatrixProps = {
  xAxisLabels: string[];
  xAxisTitle: string;
  matrix: { [k in RateAdjusters]: string[] };
  prevMatrix: { [k in RateAdjusters]: string[] };
  editMatrix: (key: RateAdjusters, i: number, value: string) => void;
  editing: boolean;
};
const DataMatrix: React.FC<DataMatrixProps> = ({
  xAxisLabels,
  xAxisTitle,
  matrix,
  prevMatrix,
  editMatrix,
  editing,
}) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th colSpan={xAxisLabels.length + 1}>{xAxisTitle}</th>
      </tr>
      <tr>
        <th></th>
        {xAxisLabels.map(tag => (
          <th key={tag}>{formatPercent(Number(tag) * 100, 2)}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {Object.keys(matrix)
        .sort()
        .map(key => {
          return (
            <Row
              key={key}
              yLabel={key as RateAdjusters}
              rowData={matrix[key]}
              prevRowData={prevMatrix[key]}
              editing={editing}
              editMatrix={editMatrix}
            />
          );
        })}
    </tbody>
  </table>
);

export default DataMatrix;
